import { STORAGE_KEY } from "@constants";
import { getCashbackPromptConfig } from "@constants/cashback-prompt";
import { RewardInfoData } from "@hooks/useGetRewardsInfo";
import type { CashbackPromptConstraints } from "@types";
import { RewardStatus } from "@types";
import logger from "@utils/logger";
import BigNumber from "bignumber.js";
import { WalletTxMethod, bnOrZero } from "kz-ui-sdk";

/**
 * Calculate total claimable cashback from reward data
 * Sums up claimable amounts from Instant, Daily, Weekly, and Monthly cashback
 * @param rewardData - Reward data from useGetRewardsInfo
 * @returns Total claimable cashback amount
 */
export function calculateTotalClaimableCashback(
  rewardData: Record<WalletTxMethod, RewardInfoData | null> | null,
): number {
  if (!rewardData) {
    return 0;
  }

  const cashbackTypes = [
    WalletTxMethod.InstantCashBack,
    WalletTxMethod.DailyCashBack,
    WalletTxMethod.WeeklyCashBack,
    WalletTxMethod.MonthlyCashBack,
  ];

  let total = bnOrZero(0);

  for (const type of cashbackTypes) {
    const rewardInfo = rewardData[type];
    if (rewardInfo?.claimPoints && rewardInfo.claimPoints > 0) {
      // Check if it's actually claimable (not locked)
      if (rewardInfo.initialStatus === RewardStatus.CLAIMABLE || rewardInfo.remainingSeconds === 0) {
        // Check minimum claim points if set
        if (!rewardInfo.minClaimPoints || rewardInfo.claimPoints >= rewardInfo.minClaimPoints) {
          total = total.plus(rewardInfo.claimPoints);
        }
      }
    }
  }

  return total.toNumber();
}

/**
 * @deprecated This function is deprecated. Eligibility checking is now handled by the backend API.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Calculate low balance threshold from last deposit amount and percentage
 * @param lastDepositAmount - Last deposit amount in base currency units
 * @param percentage - Percentage of deposit to use as threshold (e.g., 20 for 20%)
 * @returns Low balance threshold amount
 */
export function calculateLowBalanceThreshold(lastDepositAmount: number | null | undefined, percentage: number): number {
  if (!lastDepositAmount || lastDepositAmount <= 0) {
    return 0;
  }
  return bnOrZero(lastDepositAmount).multipliedBy(percentage).dividedBy(100).toNumber();
}

/**
 * @deprecated This function is deprecated. Eligibility checking is now handled by the backend API.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Check if current balance is below the low balance threshold
 * @param balance - Current balance in base currency units
 * @param lastDepositAmount - Last deposit amount in base currency units
 * @param percentage - Percentage of deposit to use as threshold
 * @returns true if balance is below threshold
 */
export function checkLowBalanceThreshold(
  balance: number | BigNumber | null | undefined,
  lastDepositAmount: number | null | undefined,
  percentage: number,
): boolean {
  if (!balance || bnOrZero(balance).lte(0)) {
    logger._console.debug("[CashbackPrompt] Low balance check: Zero or negative balance, considered low");
    return true; // Consider zero balance as low balance
  }

  const threshold = calculateLowBalanceThreshold(lastDepositAmount, percentage);
  if (threshold === 0) {
    logger._console.debug("[CashbackPrompt] Low balance check: No threshold set (no deposit)", {
      lastDepositAmount,
      percentage,
    });
    return false; // No threshold set, don't show prompt
  }

  const balanceNum = bnOrZero(balance).toNumber();
  const isLow = bnOrZero(balance).lte(threshold);

  logger._console.debug("[CashbackPrompt] Low balance check", {
    balance: balanceNum,
    lastDepositAmount,
    percentage,
    threshold,
    isLow,
  });

  return isLow;
}

/**
 * @deprecated This function is deprecated. Eligibility checking is now handled by the backend API.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Check if total claimable cashback meets the minimum threshold
 * @param rewardData - Reward data from useGetRewardsInfo
 * @param minimumAmount - Minimum claimable amount threshold
 * @returns true if total claimable >= minimum threshold
 */
export function checkCashbackThreshold(
  rewardData: Record<WalletTxMethod, RewardInfoData | null> | null,
  minimumAmount: number,
): boolean {
  const totalClaimable = calculateTotalClaimableCashback(rewardData);
  const meetsThreshold = totalClaimable >= minimumAmount;

  logger._console.debug("[CashbackPrompt] Cashback threshold check", {
    totalClaimable,
    minimumAmount,
    meetsThreshold,
  });

  return meetsThreshold;
}

/**
 * @deprecated This function is deprecated. Cooldown management is now handled by the backend API via Redis.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Get cooldown timestamp from storage
 * @returns Timestamp in milliseconds, or null if no cooldown set
 */
export function getCooldownTimestamp(): number | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY.CASHBACK_PROMPT_COOLDOWN);
    if (!stored) {
      return null;
    }
    const timestamp = parseInt(stored, 10);
    return isNaN(timestamp) ? null : timestamp;
  } catch (error) {
    logger._console.debug("[CashbackPrompt] Failed to get cooldown timestamp from sessionStorage", error);
    return null;
  }
}

/**
 * @deprecated This function is deprecated. Cooldown management is now handled by the backend API via Redis.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Set cooldown timestamp in storage
 * @param timestamp - Timestamp in milliseconds (defaults to now)
 */
export function setCooldownTimestamp(timestamp?: number): void {
  try {
    const cooldownTime = timestamp ?? Date.now();
    sessionStorage.setItem(STORAGE_KEY.CASHBACK_PROMPT_COOLDOWN, cooldownTime.toString());
    logger._console.debug("[CashbackPrompt] Cooldown timestamp set", {
      timestamp: cooldownTime,
      date: new Date(cooldownTime).toISOString(),
    });
  } catch (error) {
    logger._console.debug("[CashbackPrompt] Failed to set cooldown timestamp in sessionStorage", error);
  }
}

/**
 * @deprecated This function is deprecated. Cooldown management is now handled by the backend API via Redis.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Clear cooldown timestamp from storage
 */
export function clearCooldownTimestamp(): void {
  try {
    const hadCooldown = sessionStorage.getItem(STORAGE_KEY.CASHBACK_PROMPT_COOLDOWN) !== null;
    sessionStorage.removeItem(STORAGE_KEY.CASHBACK_PROMPT_COOLDOWN);
    logger._console.debug("[CashbackPrompt] Cooldown timestamp cleared", { hadCooldown });
  } catch (error) {
    logger._console.debug("[CashbackPrompt] Failed to clear cooldown timestamp from sessionStorage", error);
  }
}

/**
 * @deprecated This function is deprecated. Cooldown management is now handled by the backend API via Redis.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Check if cooldown period has passed
 * @param cooldownSeconds - Cooldown period in seconds
 * @returns true if cooldown has passed or no cooldown is set
 */
export function checkCooldown(cooldownSeconds: number): boolean {
  const cooldownTimestamp = getCooldownTimestamp();
  if (!cooldownTimestamp) {
    logger._console.debug("[CashbackPrompt] Cooldown check: No cooldown set, can show prompt");
    return true; // No cooldown set, can show prompt
  }

  const now = Date.now();
  const elapsedSeconds = (now - cooldownTimestamp) / 1000;
  const passed = elapsedSeconds >= cooldownSeconds;

  logger._console.debug("[CashbackPrompt] Cooldown check", {
    cooldownSeconds,
    elapsedSeconds: elapsedSeconds.toFixed(2),
    passed,
    cooldownSetAt: new Date(cooldownTimestamp).toISOString(),
  });

  return passed;
}

/**
 * @deprecated This function is deprecated. Eligibility checking is now handled by the backend API.
 * Use `useLazyGetCashbackMiniPromptEligibilityQuery` from `@api` instead.
 * Check if all constraints are met for showing the cashback prompt
 * @param balance - Current balance
 * @param lastDepositAmount - Last deposit amount
 * @param rewardData - Reward data from useGetRewardsInfo
 * @param constraints - Optional constraints (uses default if not provided)
 * @returns Object with eligibility result and details
 */
export function isEligibleForPrompt(
  balance: number | BigNumber | null | undefined,
  lastDepositAmount: number | null | undefined,
  rewardData: Record<WalletTxMethod, RewardInfoData | null> | null,
  constraints?: CashbackPromptConstraints,
): {
  eligible: boolean;
  reasons: {
    lowBalance: boolean;
    cashbackThreshold: boolean;
    cooldown: boolean;
  };
} {
  const config = getCashbackPromptConfig();
  const effectiveConstraints = constraints ?? config.constraints;

  const lowBalance = checkLowBalanceThreshold(balance, lastDepositAmount, effectiveConstraints.lowBalancePercentage);
  const cashbackThreshold = checkCashbackThreshold(rewardData, effectiveConstraints.minimumClaimableAmount);
  const cooldown = checkCooldown(effectiveConstraints.cooldownSeconds);

  const eligible = lowBalance && cashbackThreshold && cooldown;

  return {
    eligible,
    reasons: {
      lowBalance,
      cashbackThreshold,
      cooldown,
    },
  };
}
